import { ActionIcon, Button, Flex, Group, Indicator, Stack, Switch, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ChartFilterChip } from "../ChartFilterChip/ChartFilterChip";

import { Modal, TextSerif, TimeframeInput } from "@/components/elements";
import { getColorFromName } from "@/components/elements/PerformanceChart/utils";
import { IconFilter, IconFilterFilled } from "@/components/icons";

import type { TimeframeRange } from "@/components/elements/TimeframeInput/types";
import type { DatesRange } from "@/types/entity";
import type { FC } from "react";

const switchPointerStyles = {
  root: { cursor: "pointer" },
  track: { cursor: "pointer" },
  thumb: { cursor: "pointer" },
};

export type ChartFiltersModalProps = {
  conditionOptions: { label: string; value: string }[];
  dateRange?: DatesRange;
  hasActiveFilters?: boolean;
  onCancelAllFilters?: () => void;
  onConditionsChange?: (values: string[]) => void;
  onResultsChange?: (values: string[]) => void;
  onShowOutliersChange?: (value: boolean) => void;
  onShowReplicasChange?: (value: boolean) => void;
  onTimeframeChange?: (range?: DatesRange) => void;
  onTimeframeTouchedChange?: (touched: boolean) => void;
  resultOptions: { label: string; value: string }[];
  selectedConditions: string[];
  selectedResults: string[];
  showOutliers: boolean;
  showReplicas: boolean;
};

type FilterDraft = {
  selectedConditions: string[];
  selectedResults: string[];
  showOutliers: boolean;
  showReplicas: boolean;
};

function datesRangeToDefaultValue(dateRange?: DatesRange) {
  if (!dateRange) return "all" as const;
  return [dateRange.from, dateRange.to] as TimeframeRange;
}

function toggleValue(values: string[], value: string) {
  return values.includes(value) ? values.filter((item) => item !== value) : [...values, value];
}

export const ChartFiltersModal: FC<ChartFiltersModalProps> = ({
  conditionOptions,
  dateRange,
  hasActiveFilters = false,
  onCancelAllFilters,
  onConditionsChange,
  onResultsChange,
  onShowOutliersChange,
  onShowReplicasChange,
  onTimeframeChange,
  onTimeframeTouchedChange,
  resultOptions,
  selectedConditions,
  selectedResults,
  showOutliers,
  showReplicas,
}) => {
  const { t } = useTranslation();
  const [opened, { close, open }] = useDisclosure(false);
  const [draft, setDraft] = useState<FilterDraft>({
    selectedConditions,
    selectedResults,
    showOutliers,
    showReplicas,
  });
  const [draftTimeframe, setDraftTimeframe] = useState<TimeframeRange | undefined>();
  const [timeframeKey, setTimeframeKey] = useState(0);

  const hasVisibilityFilters = !!(onShowOutliersChange && onShowReplicasChange);
  const hasConditionFilters = !!(onConditionsChange && conditionOptions.length > 0);
  const hasResultFilters = !!(onResultsChange && resultOptions.length > 0);
  const hasTimeframe = !!onTimeframeChange;

  const hasDraftActiveFilters =
    (hasVisibilityFilters && (!draft.showOutliers || !draft.showReplicas)) ||
    draft.selectedConditions.length > 0 ||
    draft.selectedResults.length > 0 ||
    !!(draftTimeframe?.[0] && draftTimeframe?.[1]);

  useEffect(() => {
    if (!opened) return;

    setDraft({
      selectedConditions,
      selectedResults,
      showOutliers,
      showReplicas,
    });
    setDraftTimeframe(dateRange ? ([dateRange.from, dateRange.to] as TimeframeRange) : undefined);
    setTimeframeKey((current) => current + 1);
  }, [dateRange, opened, selectedConditions, selectedResults, showOutliers, showReplicas]);

  function handleOpen() {
    open();
  }

  function handleDismiss() {
    close();
  }

  function handleCancelFilters() {
    if (onCancelAllFilters) {
      onCancelAllFilters();
      close();
      return;
    }

    if (hasVisibilityFilters) {
      onShowReplicasChange?.(true);
      onShowOutliersChange?.(true);
    }
    if (hasConditionFilters) {
      onConditionsChange?.([]);
    }
    if (hasResultFilters) {
      onResultsChange?.([]);
    }
    if (hasTimeframe) {
      onTimeframeChange?.();
      onTimeframeTouchedChange?.(false);
    }
    close();
  }

  function handleApply() {
    if (hasVisibilityFilters) {
      onShowReplicasChange?.(draft.showReplicas);
      onShowOutliersChange?.(draft.showOutliers);
    }
    if (hasConditionFilters) {
      onConditionsChange?.(draft.selectedConditions);
    }
    if (hasResultFilters) {
      onResultsChange?.(draft.selectedResults);
    }
    if (hasTimeframe) {
      if (draftTimeframe?.[0] && draftTimeframe?.[1]) {
        onTimeframeChange?.({ from: draftTimeframe[0] as string, to: draftTimeframe[1] as string });
        onTimeframeTouchedChange?.(true);
      } else {
        onTimeframeChange?.();
        onTimeframeTouchedChange?.(false);
      }
    }
    close();
  }

  return (
    <>
      <Indicator color="warning" disabled={!hasActiveFilters} offset={4} processing size={8}>
        <ActionIcon
          aria-label={t("components.AuctionsChart.filters")}
          color={hasActiveFilters ? "primary" : "gray"}
          onClick={handleOpen}
          size="lg"
          variant={hasActiveFilters ? "filled" : "light"}
        >
          {hasActiveFilters ? <IconFilterFilled size={18} /> : <IconFilter size={18} />}
        </ActionIcon>
      </Indicator>

      <Modal
        centered
        icon={IconFilter}
        onClose={handleDismiss}
        opened={opened}
        title={t("components.AuctionsChart.filters")}
        titleFontSize="1rem"
        fullScreen={false}
      >
        <Stack gap="lg">
          {hasVisibilityFilters && (
            <Stack gap="lg">
              <Group gap="md" justify="space-between" wrap="nowrap">
                <Text size="sm" style={{ cursor: "pointer" }}>
                  {t("components.AuctionsChart.show_replicas")}
                </Text>
                <Switch
                  checked={draft.showReplicas}
                  onChange={(event) => {
                    const checked = event.currentTarget.checked;
                    setDraft((current) => ({ ...current, showReplicas: checked }));
                  }}
                  styles={switchPointerStyles}
                />
              </Group>
              <Group gap="lg" justify="space-between" wrap="nowrap">
                <Text size="sm" style={{ cursor: "pointer" }}>
                  {t("components.AuctionsChart.show_outliers")}
                </Text>
                <Switch
                  checked={draft.showOutliers}
                  onChange={(event) => {
                    const checked = event.currentTarget.checked;
                    setDraft((current) => ({ ...current, showOutliers: checked }));
                  }}
                  styles={switchPointerStyles}
                />
              </Group>
            </Stack>
          )}

          {hasTimeframe && (
            <TimeframeInput
              key={timeframeKey}
              defaultValue={datesRangeToDefaultValue(dateRange)}
              inlinePicker
              label={t("components.PerformanceChart.timeframe")}
              maxDate={new Date()}
              onChange={setDraftTimeframe}
              presets={["all", "ytd", "1y", "3y", "5y"]}
              type="month"
              valueFormat="MMM YYYY"
              w="100%"
            />
          )}

          {hasConditionFilters && (
            <Stack gap="sm">
              <TextSerif c="dimmed" size="sm">
                {t("components.AuctionsChart.conditions_filter")}
              </TextSerif>
              <Flex gap="sm" wrap="wrap">
                {conditionOptions.map(({ label, value }) => (
                  <ChartFilterChip
                    key={value}
                    color={getColorFromName(value) ?? "#94a3b8"}
                    label={label}
                    selected={draft.selectedConditions.includes(value)}
                    onClick={() =>
                      setDraft((current) => ({
                        ...current,
                        selectedConditions: toggleValue(current.selectedConditions, value),
                      }))
                    }
                  />
                ))}
              </Flex>
            </Stack>
          )}

          {hasResultFilters && (
            <Stack gap="sm">
              <TextSerif c="dimmed" size="sm">
                {t("components.AuctionsChart.result_type_filter")}
              </TextSerif>
              <Flex gap="sm" wrap="wrap">
                {resultOptions.map(({ label, value }) => (
                  <ChartFilterChip
                    key={value}
                    label={label}
                    selected={draft.selectedResults.includes(value)}
                    onClick={() =>
                      setDraft((current) => ({
                        ...current,
                        selectedResults: toggleValue(current.selectedResults, value),
                      }))
                    }
                  />
                ))}
              </Flex>
            </Stack>
          )}

          <Stack gap="sm">
            <Button disabled={!hasDraftActiveFilters} fullWidth onClick={handleApply} type="button">
              {t("components.AuctionsChart.apply_filters")}
            </Button>
            <Button fullWidth onClick={handleCancelFilters} type="button" variant="outline">
              {t("components.AuctionsChart.cancel_filters")}
            </Button>
          </Stack>
        </Stack>
      </Modal>
    </>
  );
};
